import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShoppingBag, Download, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandHeader from "@/components/BrandHeader";
import BrandFooter from "@/components/BrandFooter";
import EmailSignup from "@/components/EmailSignup";
import BuyModal from "@/components/BuyModal";
import { track, EVENTS } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const products = [
  {
    id: "7-day",
    title: "Free 7-Day Discipline Challenge",
    price_label: "Free",
    tagline: "Lock in focus. Reclaim your edge.",
    body: "A one-week challenge to prove to yourself you can keep a promise for seven days in a row. Prompts, reflection, and a habit tracker.",
    cover: `${API}/product-cover/7-day`,
    free: true,
    href: `${API}/download-challenge`,
  },
  {
    id: "30-day",
    title: "30-Day Discipline Journal",
    price_label: "$19",
    tagline: "The No-Excuse Daily Discipline Challenge.",
    body: "A full month of morning prompts, evening reflection, and a habit tracker built to be forgiving. Print it, mark it up, live it.",
    cover: `${API}/product-cover/30-day`,
  },
  {
    id: "90-day",
    title: "90-Day Discipline Journal",
    price_label: "$39",
    tagline: "The long version — the one that changes who you are.",
    body: "Ninety days. One quiet promise, kept over and over, until it becomes an identity. The flagship journal for people ready to do the real work.",
    cover: `${API}/product-cover/90-day`,
  },
];

export default function Shop() {
  const [modalProduct, setModalProduct] = useState(null);
  return (
    <div className="min-h-screen bg-white text-[#333333]" data-testid="shop-page">
      <BrandHeader variant="cream" />

      <section className="bg-[#F8F6F2] pt-16 pb-16 md:pt-24 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4 flex items-center gap-2"><ShoppingBag className="h-3.5 w-3.5" /> Shop</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[4rem] font-medium tracking-tight text-[#333333] leading-[1.05] max-w-4xl">
            Beautiful tools for the <span className="italic">everyday practice</span> of wellness.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[#4A4A4A] max-w-2xl leading-relaxed">
            Three discipline journals — start free, then grow. Instant PDF download after checkout.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {products.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: (i % 3) * 0.06, ease: EASE }}
              className="group bg-[#F8F6F2] rounded-3xl overflow-hidden border border-[#EDE9E1] flex flex-col hover:shadow-xl transition-all duration-300"
              data-testid={`shop-product-${p.id}`}
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-white">
                <img
                  src={p.cover}
                  alt={`${p.title} cover`}
                  className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                  loading={i === 0 ? "eager" : "lazy"}
                />
                <div className="absolute top-4 left-4">
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1.5 ${p.free ? "bg-[#8FAF8A] text-white" : "bg-white text-[#333333] border border-[#EDE9E1]"}`}>
                    {p.free ? "Free" : p.price_label}
                  </span>
                </div>
              </div>
              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#8FAF8A]">{p.tagline}</div>
                <h3 className="mt-2 font-display text-xl md:text-2xl font-bold text-[#333333]">{p.title}</h3>
                <p className="mt-3 text-[#4A4A4A] text-sm leading-relaxed flex-1">{p.body}</p>
                <div className="mt-6 flex items-center justify-between gap-3">
                  <span className="font-serif-display font-medium text-2xl text-[#333333]">{p.price_label}</span>
                  {p.free ? (
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => track(EVENTS.FREE_PDF_DOWNLOADED, { from: "shop", product_id: p.id })}
                      className="inline-flex items-center gap-2 rounded-full bg-[#333333] hover:bg-black text-white h-11 px-5 text-sm font-semibold whitespace-nowrap transition-all"
                      data-testid={`shop-cta-${p.id}`}
                    >
                      <Download className="h-4 w-4" /> Get Free
                    </a>
                  ) : (
                    <Button
                      onClick={() => { setModalProduct(p); track(EVENTS.BUY_MODAL_OPENED, { product_id: p.id, from: "shop" }); }}
                      className="rounded-full bg-[#333333] hover:bg-black text-white h-11 px-5 text-sm font-semibold whitespace-nowrap"
                      data-testid={`shop-cta-${p.id}`}
                    >
                      Buy Now <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="notify" className="py-20 md:py-24 bg-[#F8F6F2]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#8FAF8A]/15 text-[#6E8F6A] mb-6">
            <Sparkles className="h-6 w-6" />
          </span>
          <h2 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#333333]">
            One quiet email a week.
          </h2>
          <p className="mt-4 text-[#4A4A4A]">Encouragement, new-product announcements, and the occasional discount — never spam.</p>
          <div className="mt-8 max-w-2xl mx-auto text-left">
            <EmailSignup source="shop" testIdPrefix="join" overline={null} headline={null} subtext={null} compact />
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 md:px-10 pb-16 text-center text-sm text-[#8A8A8A]">
        Secure checkout by Stripe. Digital PDFs are delivered instantly on the success page and by email —{" "}
        <Link to="/terms" className="underline hover:text-[#333333]">see terms</Link>.
      </div>

      <BrandFooter />

      <BuyModal
        open={!!modalProduct}
        onOpenChange={(v) => { if (!v) setModalProduct(null); }}
        product={modalProduct}
      />
    </div>
  );
}
